/**
 * Queries of the master data (docs/08 §6): settings, points, days, persons, users, movement types.
 * Keys start with the SSE entity, so every change made elsewhere refreshes them.
 */
import type {
  ItemsResponse,
  MovementType,
  OperatingDayWithHours,
  Person,
  Point,
  PublicSettings,
  Settings,
  User,
} from 'core';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

const MASTER_STALE_MS = 5 * 60_000;

const items = async <T>(path: string): Promise<T[]> =>
  (await api.get<ItemsResponse<T>>(path)).items;

/** Points sorted like the workbook (`sortOrder`, then id). */
export function sortPoints(points: readonly Point[]): Point[] {
  return [...points].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

export const pointsQuery = {
  queryKey: ['points'] as const,
  queryFn: async () => sortPoints(await items<Point>('/points')),
  staleTime: MASTER_STALE_MS,
};

export const personsQuery = {
  queryKey: ['persons'] as const,
  queryFn: () => items<Person>('/persons'),
  staleTime: MASTER_STALE_MS,
};

export const usersQuery = {
  queryKey: ['users'] as const,
  queryFn: () => items<User>('/users'),
};

export const daysQuery = {
  queryKey: ['days'] as const,
  queryFn: async () =>
    (await items<OperatingDayWithHours>('/days')).sort((a, b) => a.date.localeCompare(b.date)),
  staleTime: MASTER_STALE_MS,
};

export const movementTypesQuery = {
  queryKey: ['movement-types'] as const,
  queryFn: async () => (await items<MovementType>('/movement-types')).sort((a, b) => a.id - b.id),
  staleTime: MASTER_STALE_MS,
};

/** Full settings for admin/supervisor, `PublicSettings` for the other roles. */
export const settingsQuery = {
  queryKey: ['settings'] as const,
  queryFn: () => api.get<Settings | PublicSettings>('/settings'),
  staleTime: MASTER_STALE_MS,
};

export const usePoints = () => useQuery(pointsQuery);
export const usePersons = () => useQuery(personsQuery);
export const useDays = () => useQuery(daysQuery);
export const useMovementTypes = () => useQuery(movementTypesQuery);
export const useSettings = () => useQuery(settingsQuery);
export const useUsers = (enabled = true) => useQuery({ ...usersQuery, enabled });
